import { useEffect, useState } from "react";
import {
  fetchFuelCategorySettings,
  updateFuelCategorySettings,
} from "../api/client.js";
import { useTranslation } from "react-i18next";

export default function AdminFuelCategories() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadCategories();
  }, []);

  async function loadCategories() {
    setLoading(true);
    setError("");
    try {
      const data = await fetchFuelCategorySettings();
      setCategories(data);
    } catch (err) {
      console.error("Failed to load fuel categories", err);
      setError(t("admin.fuel.errors.load"));
    } finally {
      setLoading(false);
    }
  }

  function toggleCategory(id) {
    setCategories((prev) =>
      prev.map((category) =>
        category.id === id ? { ...category, active: !category.active } : category
      )
    );
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const activeIds = categories.filter((cat) => cat.active).map((cat) => cat.id);
      const res = await updateFuelCategorySettings(activeIds);
      setCategories(res.categories);
      setSuccess(t("admin.fuel.success"));
    } catch (err) {
      console.error("Failed to update categories", err);
      setError(err.response?.data?.message || t("admin.fuel.errors.save"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="bg-white border rounded-xl shadow p-5">
        <h1 className="text-2xl font-semibold mb-3">{t("admin.fuel.title")}</h1>
        <p className="text-sm text-gray-600 mb-4">
          {t("admin.fuel.description")}
        </p>

        {loading ? (
          <p className="text-gray-500 text-sm">{t("admin.fuel.loading")}</p>
        ) : (
          <div className="space-y-2">
            {categories.map((category) => (
              <label
                key={category.id}
                className="flex items-center justify-between border rounded-lg px-4 py-3"
              >
                <div>
                  <div className="font-medium">{category.label}</div>
                  <div className="text-xs text-gray-500">{category.id}</div>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5"
                  checked={category.active}
                  onChange={() => toggleCategory(category.id)}
                />
              </label>
            ))}
            {categories.length === 0 && (
              <div className="text-sm text-gray-500">{t("admin.fuel.none")}</div>
            )}
          </div>
        )}

        {error && <p className="text-sm text-red-600 mt-4">{error}</p>}
        {success && <p className="text-sm text-green-600 mt-4">{success}</p>}

        <div className="mt-4 flex gap-2">
          <button
            className="px-4 py-2 rounded-lg border"
            onClick={loadCategories}
            disabled={loading || saving}
          >
            {t("admin.fuel.reload")}
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-60"
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? t("admin.fuel.saving") : t("admin.fuel.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
